import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import api from '../utils/api';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { BarChart3, Award, Users, Hourglass, History, Trophy, CalendarClock, Loader2 } from 'lucide-react';

const fmt = (n) => (n ?? 0).toLocaleString('en-IN');
const fmtDT = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso || '—'; } };
const fmtD = (iso) => { try { return new Date(iso).toLocaleDateString(); } catch { return iso || '—'; } };

export default function RewardReportsPage({ user, onLogout }) {
  const [tab, setTab] = useState('overview');
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [patientWise, setPatientWise] = useState([]);
  const [history, setHistory] = useState([]);
  const [most, setMost] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [expiring, setExpiring] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, b, c, d, e, f] = await Promise.all([
        api.get('/admin/reward-reports/summary'),
        api.get('/admin/reward-reports/patient-wise'),
        api.get('/admin/reward-reports/redemption-history'),
        api.get('/admin/reward-reports/most-redeemed'),
        api.get('/admin/reward-reports/upcoming-reward-appointments'),
        api.get('/admin/reward-reports/expiring-soon?days=30'),
      ]);
      setSummary(a.data); setPatientWise(b.data || []); setHistory(c.data || []);
      setMost(d.data || []); setUpcoming(e.data || []); setExpiring(f.data || []);
    } catch (err) { toast.error('Could not load reports'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        <header className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <BarChart3 className="w-6 h-6 text-indigo-600" />
              Reward Reports
            </h1>
            <p className="text-slate-500 text-sm mt-1">Points issued, redeemed, expired — no rupee figures.</p>
          </div>
          <Button variant="outline" className="rounded-xl" onClick={load} data-testid="reports-refresh-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
            Refresh
          </Button>
        </header>

        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Total Earned"    value={summary.total_points_earned}    color="text-emerald-700" icon={<Award className="w-4 h-4 text-emerald-600" />} testid="report-total-earned" />
            <Stat label="Total Redeemed"  value={summary.total_points_redeemed}  color="text-purple-700"  icon={<Trophy className="w-4 h-4 text-purple-600" />} testid="report-total-redeemed" />
            <Stat label="Total Expired"   value={summary.total_points_expired}   color="text-rose-700"    icon={<Hourglass className="w-4 h-4 text-rose-600" />} testid="report-total-expired" />
            <Stat label="Active Balance"  value={summary.total_points_available} color="text-amber-700"   icon={<Users className="w-4 h-4 text-amber-600" />} testid="report-total-available" />
          </div>
        )}

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid grid-cols-2 md:grid-cols-5 rounded-xl">
            <TabsTrigger value="overview" data-testid="tab-overview">Patient-wise</TabsTrigger>
            <TabsTrigger value="history" data-testid="tab-history">Redemption History</TabsTrigger>
            <TabsTrigger value="most" data-testid="tab-most">Most Redeemed</TabsTrigger>
            <TabsTrigger value="upcoming" data-testid="tab-upcoming">Upcoming</TabsTrigger>
            <TabsTrigger value="expiring" data-testid="tab-expiring">Expiring 30d</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-4">
            <Table cols={['Patient', 'Phone', 'Available', 'Lifetime', 'Redeemed', 'Expired']}
                   empty="No patient wallets yet."
                   rows={patientWise.map(p => [
                     p.patient_name || '—',
                     p.patient_phone || '—',
                     fmt(p.available_points),
                     fmt(p.lifetime_points_earned),
                     fmt(p.redeemed_points),
                     fmt(p.expired_points),
                   ])} />
          </TabsContent>

          <TabsContent value="history" className="mt-4">
            <Table cols={['When', 'Patient', 'Reward', 'Points', 'Appointment']}
                   empty="No redemptions yet."
                   rows={history.map(h => [
                     fmtDT(h.created_at), h.patient_name || '—',
                     h.reward_name || '—',
                     <span className="text-purple-700 font-semibold">{fmt(Math.abs(h.points_change))}</span>,
                     h.appointment_id ? <code className="text-xs">{h.appointment_id.slice(0,8)}…</code> : '—',
                   ])} />
          </TabsContent>

          <TabsContent value="most" className="mt-4">
            <Table cols={['Reward', 'Times Redeemed', 'Points Issued Out']}
                   empty="Nothing redeemed yet."
                   rows={most.map(r => [r.reward_name, fmt(r.redemption_count), fmt(r.points_redeemed_total)])} />
          </TabsContent>

          <TabsContent value="upcoming" className="mt-4">
            <Table cols={['Date', 'Time', 'Patient', 'Reward', 'Status']}
                   empty="No upcoming reward appointments."
                   rows={upcoming.map(a => [
                     fmtD(a.scheduled_date), a.scheduled_time || '—',
                     a.patient_name, a.reward_name,
                     <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">{a.status}</Badge>,
                   ])} />
          </TabsContent>

          <TabsContent value="expiring" className="mt-4">
            <Table cols={['Patient', 'Phone', 'Points', 'Expires On']}
                   empty="No points expiring in the next 30 days."
                   rows={expiring.map(b => [
                     b.patient_name || '—', b.patient_phone || '—',
                     <span className="font-semibold text-amber-700">{fmt(b.points_available)}</span>,
                     fmtD(b.expires_at),
                   ])} />
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}

const Stat = ({ label, value, color, icon, testid }) => (
  <Card className="p-4 rounded-2xl border-slate-200" data-testid={testid}>
    <div className="flex items-center justify-between mb-1">
      <span className="text-xs uppercase tracking-wide text-slate-500">{label}</span>
      {icon}
    </div>
    <div className={`text-2xl font-bold ${color}`}>{fmt(value)}</div>
  </Card>
);

const Table = ({ cols, rows, empty }) => (
  <Card className="rounded-2xl border-slate-200 overflow-hidden">
    {rows.length === 0 ? (
      <div className="text-center py-10 text-sm text-slate-500">{empty}</div>
    ) : (
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
          <tr>
            {cols.map(c => <th key={c} className="text-left px-4 py-2">{c}</th>)}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((r, i) => (
            <tr key={i} className="hover:bg-slate-50">
              {r.map((cell, j) => <td key={j} className="px-4 py-2">{cell}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </Card>
);
